/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { ErrorModel, OptionType } from '@/shared/api/BaseAPI.model'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from '@/shared/components/Snackbar/SnackbarProvider'
import { t } from 'i18next'
import {
  dishSizePayloadModel,
  dishSizePutPayloadModel,
  QUERY
} from '../api/SidebarAccordionAPI.model'
import SidebarAccordionAPI from '../api/SidebarAccordionAPI'
import { useSelectedLanguagesQuery } from './useSelectedLanguagesQuery'

export function useDishSizesQuery() {
  const queryClient = useQueryClient()
  const { showSnackbar } = useSnackbar()
  const currentLanguage =
    useTranslation().i18n.language || localStorage.getItem('i18nextLng') || ''
  const { defaultLanguage } = useSelectedLanguagesQuery(currentLanguage)

  const dishSizesList = useQuery({
    queryKey: [QUERY.DISH_SIZES],
    queryFn: SidebarAccordionAPI.getDishSizes
  })

  const dishSizesOptions = useMemo(
    () =>
      dishSizesList.data?.map(size => ({
        label: size.translations.find(
          t => t.languageCode === defaultLanguage?.languageCode
        )?.name,
        value: size.id
      })) || [],
    [dishSizesList.data, defaultLanguage?.languageCode]
  ) as OptionType[]

  const { mutate: putDishSize } = useMutation<
    dishSizePutPayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (dishSizePayload: dishSizePutPayloadModel) =>
      SidebarAccordionAPI.putDishSize(
        dishSizePayload.dishSizeId,
        dishSizePayload.dishSizePayload
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_SIZES] })
      // The single-item query has its own key, so invalidating the list leaves
      // it stale and the edit form reopens on the pre-save values. Removed
      // rather than invalidated: the query client sets `refetchOnMount: false`,
      // so a stale entry would still be served as-is to the next form.
      queryClient.removeQueries({ queryKey: [QUERY.DISH_SIZE] })
      showSnackbar(
        t('ALERTS.PUT_DISH_SIZE_SUCCESS.TITLE'),
        t('ALERTS.PUT_DISH_SIZE_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.PUT_DISH_SIZE_ERROR.TITLE'),
        t('ALERTS.PUT_DISH_SIZE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: postDishSize } = useMutation<
    dishSizePayloadModel,
    ErrorModel,
    any
  >({
    mutationFn: (dishSizePayload: dishSizePayloadModel) =>
      SidebarAccordionAPI.postDishSize(dishSizePayload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_SIZES] })
      // The single-item query has its own key, so invalidating the list leaves
      // it stale and the edit form reopens on the pre-save values. Removed
      // rather than invalidated: the query client sets `refetchOnMount: false`,
      // so a stale entry would still be served as-is to the next form.
      queryClient.removeQueries({ queryKey: [QUERY.DISH_SIZE] })
      showSnackbar(
        t('ALERTS.POST_DISH_SIZE_SUCCESS.TITLE'),
        t('ALERTS.POST_DISH_SIZE_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.POST_DISH_SIZE_ERROR.TITLE'),
        t('ALERTS.POST_DISH_SIZE_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  const { mutate: deleteDishSize } = useMutation<string, ErrorModel, any>({
    mutationFn: (dishSizeId: string) =>
      SidebarAccordionAPI.deleteDishSize(dishSizeId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY.DISH_SIZES] })
      // The single-item query has its own key, so invalidating the list leaves
      // it stale and the edit form reopens on the pre-save values. Removed
      // rather than invalidated: the query client sets `refetchOnMount: false`,
      // so a stale entry would still be served as-is to the next form.
      queryClient.removeQueries({ queryKey: [QUERY.DISH_SIZE] })
      showSnackbar(
        t('ALERTS.DELETE_DISH_SIZES_SUCCESS.TITLE'),
        t('ALERTS.DELETE_DISH_SIZES_SUCCESS.DESCRIPTION'),
        'success'
      )
    },
    onError: () => {
      showSnackbar(
        t('ALERTS.DELETE_DISH_SIZES_ERROR.TITLE'),
        t('ALERTS.DELETE_DISH_SIZES_ERROR.DESCRIPTION'),
        'error'
      )
    }
  })

  return {
    dishSizesOptions,
    dishSizesList,
    putDishSize,
    postDishSize,
    deleteDishSize
  }
}
